import React, { useState, useEffect, useMemo } from 'react';
import { Mail, Phone, Lock, User as UserIcon, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { authService, type UserCredentials } from '@/features/auth/services/authService';
import { usePageFullScreenBackground } from '@/hooks/useFullScreenBackground';
import { notificationService } from '@/services/notificationService';
import { playClickSound } from '@/utils/soundUtils';
import AuthHeader from '@/layouts/components/Header/AuthHeader';
import { CountrySelectWithSearch } from './CountrySelectWithSearch';
import { useTranslation } from 'react-i18next';
import './PhoneInputAuth.css';
import {
    type ForgotChannel,
    getForgotEmailFormatError,
    getForgotPhoneFormatError,
    getForgotNotRegisteredMessage,
    isForgotContactFormatValid,
} from '@/features/auth/utils/forgotPasswordValidation';

/** Dark login panel: navy base + cyan accent border + purple CTAs */
const loginPanelStyle: React.CSSProperties = {
    border: '1px solid rgba(56, 189, 248, 0.22)',
    background: 'linear-gradient(180deg, rgba(8, 15, 35, 0.92) 0%, rgba(5, 10, 27, 0.88) 100%)',
    boxShadow: '0 0 0 1px rgba(168, 85, 247, 0.06), 0 12px 40px rgba(0, 0, 0, 0.45)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
};

const tabActiveStyle: React.CSSProperties = {
    background: 'linear-gradient(90deg, #f857a6 0%, #a855f7 100%)',
    boxShadow: '0 0 18px rgba(168, 85, 247, 0.45)',
    borderRadius: 10,
};

const tabInactiveStyle: React.CSSProperties = {
    background: '#0c1222',
    border: '1px solid #1e2645',
    borderRadius: 10,
};

const primaryCtaStyle: React.CSSProperties = {
    background: 'linear-gradient(90deg, #7c3aed 0%, #a855f7 100%)',
    boxShadow: '0 4px 20px rgba(124, 58, 237, 0.35)',
};

/** Cooldown between RESEND taps (forgot-account OTP step); separate from OTP expiry timer */
const FORGOT_RESEND_COOLDOWN_SECONDS = 60;

interface UserAuthenticationProps {
    selectedProvider: {
        id: number | string;
        name: string;
        tagline: string;
        gradient: string;
        icon: string;
    } | null;
    selectedAction: 'deposit' | 'withdrawal' | null;
    onBack: () => void;
    onAuthenticate: (credentials: UserCredentials) => void | Promise<void>;
    onRegister?: () => void;
    totalAmount?: number;
    isWalletFlow?: boolean;
    /** When set (e.g. from PaymentCheckoutPage redirect), navigate here after successful login */
    redirectToAfterLogin?: string;
    hideHeader?: boolean; // Hide header when used in page with layout
}

type LoginTab = 'username' | 'phone' | 'email';
type ForgotStep = 1 | 2 | 3;

export default function UserAuthentication({
    selectedProvider,
    selectedAction,
    onBack,
    onAuthenticate,
    onRegister,
    totalAmount = 0,
    isWalletFlow = false,
    redirectToAfterLogin,
    hideHeader = false
}: UserAuthenticationProps) {
    const { t } = useTranslation();
    const fullScreenBg = usePageFullScreenBackground({
        minHeight: '100vh'
    });
    const navigate = useNavigate();

    // Tab state
    const [activeTab, setActiveTab] = useState<LoginTab>('username');
    const [isForgotMode, setIsForgotMode] = useState(false);
    const [forgotStep, setForgotStep] = useState<ForgotStep>(1);
    const [forgotChannel, setForgotChannel] = useState<ForgotChannel>('email');
    const [forgotContactTouched, setForgotContactTouched] = useState(false);
    const [forgotContact, setForgotContact] = useState('');
    const [forgotRequestToken, setForgotRequestToken] = useState('');
    const [forgotVerifiedToken, setForgotVerifiedToken] = useState('');
    const [forgotOtp, setForgotOtp] = useState('');
    const [forgotNewPassword, setForgotNewPassword] = useState('');
    const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
    const [showForgotNewPassword, setShowForgotNewPassword] = useState(false);
    /** OTP session expiry countdown (matches API "Code expires in") */
    const [forgotOtpCooldown, setForgotOtpCooldown] = useState(300);
    /** Separate from OTP expiry so RESEND is not blocked for the full validity window */
    const [forgotResendCooldown, setForgotResendCooldown] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');

    /** Username+password tab: credentials OK and waiting for email OTP (2FA). */
    const [username2FA, setUsername2FA] = useState(false);
    const [twoFactorEmail, setTwoFactorEmail] = useState('');
    const [twoFactorMaskedEmail, setTwoFactorMaskedEmail] = useState('');

    // Phone login states
    const [phoneNumber, setPhoneNumber] = useState('');
    const [phoneCode, setPhoneCode] = useState('');
    const [phoneCooldown, setPhoneCooldown] = useState(0);
    const [isPhoneCodeSent, setIsPhoneCodeSent] = useState(false);

    // Email login states
    const [email, setEmail] = useState('');
    const [emailCode, setEmailCode] = useState('');
    const [emailCooldown, setEmailCooldown] = useState(0);
    const [isEmailCodeSent, setIsEmailCodeSent] = useState(false);

    // Common states
    const [isSendingCode, setIsSendingCode] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

    /** True while any auth API call is in flight (login, send OTP, forgot-account steps). */
    const apiBusy = isLoading || isSendingCode;

    // Cooldown timers
    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (phoneCooldown > 0) {
            interval = setInterval(() => {
                setPhoneCooldown(prev => prev - 1);
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [phoneCooldown]);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (emailCooldown > 0) {
            interval = setInterval(() => {
                setEmailCooldown(prev => prev - 1);
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [emailCooldown]);

    useEffect(() => {
        if (!isForgotMode || forgotStep !== 2 || forgotOtpCooldown <= 0) return;
        const interval = setInterval(() => setForgotOtpCooldown((prev) => prev - 1), 1000);
        return () => clearInterval(interval);
    }, [isForgotMode, forgotStep, forgotOtpCooldown]);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (forgotResendCooldown > 0) {
            interval = setInterval(() => {
                setForgotResendCooldown((prev) => Math.max(0, prev - 1));
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [forgotResendCooldown]);

    // Ensure body and html have full height background
    useEffect(() => {
        const originalBodyBg = document.body.style.background;
        const originalHtmlBg = document.documentElement.style.background;
        const originalBodyMinHeight = document.body.style.minHeight;
        const originalHtmlMinHeight = document.documentElement.style.minHeight;

        document.body.style.background = 'linear-gradient(to bottom right, rgb(15 23 42), rgb(88 28 135), rgb(15 23 42))';
        document.body.style.minHeight = '100vh';
        document.documentElement.style.background = 'linear-gradient(to bottom right, rgb(15 23 42), rgb(88 28 135), rgb(15 23 42))';
        document.documentElement.style.minHeight = '100vh';

        return () => {
            document.body.style.background = originalBodyBg;
            document.body.style.minHeight = originalBodyMinHeight;
            document.documentElement.style.background = originalHtmlBg;
            document.documentElement.style.minHeight = originalHtmlMinHeight;
        };
    }, []);

    useEffect(() => {
    }, []);

    const handleBackClick = () => {
        if (apiBusy) {
            return;
        }
        playClickSound();
        onBack();
    };

    const validatePhoneNumber = (phone: string): boolean => {
        // Use authService validation for international format
        const phoneValidation = authService.validatePhone(phone);
        if (!phoneValidation.isValid) {
            setValidationErrors({ phone: phoneValidation.message });
            return false;
        }
        return true;
    };

    const validateEmail = (emailValue: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailValue)) {
            setValidationErrors({ email: t('auth.emailInvalid') });
            return false;
        }
        return true;
    };

    const handleSendPhoneCode = async () => {
        if (isSendingCode || phoneCooldown > 0) return;

        setValidationErrors({});
        const phone = phoneNumber.trim();

        if (!phone) {
            setValidationErrors({ phone: t('auth.phoneRequired') });
            return;
        }

        if (!validatePhoneNumber(phone)) {
            return;
        }

        setIsSendingCode(true);
        playClickSound();

        try {
            // Call API to send verification code via Twilio
            const result = await authService.sendPhoneLoginCode(phone);

            if (result.success) {
                setIsPhoneCodeSent(true);
                setPhoneCooldown(60);
                notificationService.success(t('auth.verificationSentPhone'), { duration: 5000 });
            } else {
                setValidationErrors({ general: result.message || t('auth.failedToSendCode') });
                notificationService.error(result.message || t('auth.failedToSendCode'), { duration: 10000 });
            }
        } catch (error: any) {
            const errorMessage = error?.message || t('auth.failedToSendVerification');
            setValidationErrors({ general: errorMessage });
            notificationService.error(errorMessage, { duration: 10000 });
        } finally {
            setIsSendingCode(false);
        }
    };

    const handleSendEmailCode = async () => {
        if (isSendingCode || emailCooldown > 0) return;

        setValidationErrors({});
        const emailValue = email.trim();

        if (!emailValue) {
            setValidationErrors({ email: t('auth.emailRequired') });
            return;
        }

        if (!validateEmail(emailValue)) {
            return;
        }

        setIsSendingCode(true);
        playClickSound();

        try {
            // Call API to send OTP code via email for email login
            const result = await authService.sendEmailOTP(emailValue);

            if (result.success) {
                setIsEmailCodeSent(true);
                setEmailCooldown(60);
                notificationService.success(t('auth.verificationSentEmail'), { duration: 5000 });
            } else {
                setValidationErrors({ general: result.message || t('auth.failedToSendCode') });
                notificationService.error(result.message || t('auth.failedToSendCode'), { duration: 10000 });
            }
        } catch (error: any) {
            const errorMessage = error?.message || t('auth.failedToSendVerification');
            setValidationErrors({ general: errorMessage });
            notificationService.error(errorMessage, { duration: 10000 });
        } finally {
            setIsSendingCode(false);
        }
    };

    const handlePhoneLogin = async (e: React.FormEvent) => {
        e.preventDefault();

        if (apiBusy) return;

        setValidationErrors({});
        const phone = phoneNumber.trim();
        const code = phoneCode.trim();

        if (!phone) {
            setValidationErrors({ phone: t('auth.phoneRequired') });
            return;
        }

        if (!code) {
            setValidationErrors({ phoneCode: t('auth.verificationCodeRequired') });
            return;
        }

        if (code.length !== 6) {
            setValidationErrors({ phoneCode: t('auth.verificationCodeMustBe6') });
            return;
        }

        setIsLoading(true);
        playClickSound();

        // Actual authentication logic
        try {
            const result = await authService.loginWithPhoneCode(phone, code);

            if (result.success) {

                // Token should be saved by authService.loginWithPhoneCode via apiService.setTokens()
                // Parent (e.g. UserLoginPage) handles redirect via onAuthenticate
                await Promise.resolve(
                    onAuthenticate({
                        phone: phone,
                        phoneCode: code,
                        email: '',
                        password: '',
                        emailCode: ''
                    } as UserCredentials)
                );
            } else {
                // Show error message - if error is a detailed message (contains spaces), show both
                // Otherwise, if error is just a code (like INVALID_CREDENTIALS), only show message
                let errorMessage = result.message || 'Login failed';
                if (result.error && result.error.includes(' ')) {
                    // Error is a detailed message, show both
                    errorMessage = `${errorMessage}\n${result.error}`;
                }
                setValidationErrors({ general: errorMessage });
                notificationService.error(errorMessage, { duration: 10000 });
            }
        } catch (error: any) {
            const errorMessage = error?.message || 'Login failed. Please try again.';
            setValidationErrors({ general: errorMessage });
            notificationService.error(errorMessage, { duration: 10000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleEmailLogin = async (e: React.FormEvent) => {
        e.preventDefault();

        if (apiBusy) return;

        setValidationErrors({});
        const emailValue = email.trim();
        const code = emailCode.trim();

        if (!emailValue) {
            setValidationErrors({ email: 'Email address is required' });
            return;
        }

        if (!code) {
            setValidationErrors({ emailCode: 'Verification code is required' });
            return;
        }

        if (code.length !== 6) {
            setValidationErrors({ emailCode: 'Verification code must be 6 digits' });
            return;
        }

        setIsLoading(true);
        playClickSound();

        // Actual authentication logic
        try {
            const result = await authService.loginWithEmailCode(emailValue, code);

            if (result.success) {

                // Token should be saved by authService.loginWithEmailCode via apiService.setTokens()
                // Parent (e.g. UserLoginPage) handles redirect via onAuthenticate
                await Promise.resolve(
                    onAuthenticate({
                        email: emailValue,
                        emailCode: code,
                        phone: '',
                        password: '',
                        phoneCode: ''
                    } as UserCredentials)
                );
            } else {
                // Show error message - if error is a detailed message (contains spaces), show both
                // Otherwise, if error is just a code (like INVALID_CREDENTIALS), only show message
                let errorMessage = result.message || 'Login failed';
                if (result.error && result.error.includes(' ')) {
                    // Error is a detailed message, show both
                    errorMessage = `${errorMessage}\n${result.error}`;
                }
                setValidationErrors({ general: errorMessage });
                notificationService.error(errorMessage, { duration: 10000 });
            }
        } catch (error: any) {
            const errorMessage = error?.message || 'Login failed. Please try again.';
            setValidationErrors({ general: errorMessage });
            notificationService.error(errorMessage, { duration: 10000 });
        } finally {
            setIsLoading(false);
        }
    };


    const resetUsername2FA = () => {
        setUsername2FA(false);
        setTwoFactorEmail('');
        setTwoFactorMaskedEmail('');
        setEmailCode('');
        setEmailCooldown(0);
    };

    const handleTabChange = (tab: LoginTab) => {
        if (apiBusy) return;
        playClickSound();
        setActiveTab(tab);
        setValidationErrors({});
        resetUsername2FA();
    };

    /** Sends (or resends) the 2FA email code for the username+password tab. */
    const sendUsername2FACode = async (target: string): Promise<boolean> => {
        if (isSendingCode || emailCooldown > 0) return false;
        const id = target.trim();
        if (!id) {
            const msg = t(
                'auth.twoFactorEmailUnavailable',
                'Could not send a code to your registered email. Please try again or contact support.'
            );
            setValidationErrors({ general: msg });
            notificationService.error(msg, { duration: 10000 });
            return false;
        }
        setIsSendingCode(true);
        try {
            const result = await authService.sendLoginCode(id);
            if (!result.success) {
                const msg = result.message || t('auth.failedToSendCode');
                setValidationErrors({ general: msg });
                notificationService.error(msg, { duration: 10000 });
                if (result.cooldownSeconds) setEmailCooldown(result.cooldownSeconds);
                return false;
            }
            const resolvedEmail = (
                result.email ||
                (authService.validateEmail(id).isValid ? id : twoFactorEmail) ||
                ''
            ).trim();
            const masked =
                result.maskedEmail ||
                (resolvedEmail ? authService.maskEmail(resolvedEmail) : twoFactorMaskedEmail) ||
                '';
            if (resolvedEmail) setTwoFactorEmail(resolvedEmail);
            if (masked) setTwoFactorMaskedEmail(masked);
            setEmailCooldown(result.cooldownSeconds ?? 60);
            notificationService.success(
                masked
                    ? t('auth.twoFactorCodeSent', 'Verification code sent to {{email}}', { email: masked })
                    : t('auth.verificationSentEmail'),
                { duration: 5000 }
            );
            return true;
        } catch (error: any) {
            const msg = error?.message || t('auth.failedToSendVerification');
            setValidationErrors({ general: msg });
            notificationService.error(msg, { duration: 10000 });
            return false;
        } finally {
            setIsSendingCode(false);
        }
    };

    const enterUsername2FA = async (opts: {
        email?: string;
        maskedEmail?: string;
        codeSent?: boolean;
        cooldownSeconds?: number;
    }) => {
        const emailForCode = (opts.email || '').trim();
        const masked = opts.maskedEmail || (emailForCode ? authService.maskEmail(emailForCode) : '');
        setUsername2FA(true);
        setTwoFactorEmail(emailForCode);
        setTwoFactorMaskedEmail(masked);
        setEmailCode('');
        setEmailCooldown(0);
        setValidationErrors({});

        // Backend already emailed OTP on the credential challenge.
        if (opts.codeSent) {
            setEmailCooldown(opts.cooldownSeconds ?? 60);
            notificationService.success(
                masked
                    ? t('auth.twoFactorCodeSent', 'Verification code sent to {{email}}', { email: masked })
                    : t('auth.verificationSentEmail'),
                { duration: 5000 }
            );
            return;
        }

        // Prefer registered email from API; otherwise resend via login username/identifier.
        const sendTarget = emailForCode || username.trim();
        await sendUsername2FACode(sendTarget);
    };

    const handleResendUsername2FACode = async () => {
        if (isSendingCode || emailCooldown > 0 || isLoading) return;
        playClickSound();
        await sendUsername2FACode(twoFactorEmail.trim() || username.trim());
    };

    const handleBackToPasswordStep = () => {
        if (apiBusy) return;
        playClickSound();
        resetUsername2FA();
    };

    const handleUsernameLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (apiBusy) return;
        setValidationErrors({});

        // Step 2: 2FA OTP verification
        if (username2FA) {
            const code = emailCode.trim();
            if (!code) {
                setValidationErrors({ emailCode: t('auth.verificationCodeRequired') });
                return;
            }
            if (code.length !== 6) {
                setValidationErrors({ emailCode: t('auth.verificationCodeMustBe6') });
                return;
            }
            setIsLoading(true);
            playClickSound();
            try {
                const result = await authService.authenticateUser(
                    {
                        username: username.trim(),
                        email: twoFactorEmail || '',
                        password,
                        emailCode: code,
                        phone: '',
                        phoneCode: ''
                    } as UserCredentials,
                    true
                );
                if (!result.success) {
                    let errorMessage = result.message || t('auth.loginFailed', 'Login failed');
                    if (result.error && result.error.includes(' ')) {
                        errorMessage = `${errorMessage}\n${result.error}`;
                    }
                    setValidationErrors({ general: errorMessage });
                    notificationService.error(errorMessage, { duration: 10000 });
                    return;
                }
                await Promise.resolve(
                    onAuthenticate({
                        username: username.trim(),
                        email: twoFactorEmail || '',
                        password,
                        emailCode: code,
                        phone: '',
                        phoneCode: ''
                    } as UserCredentials)
                );
            } catch (error: unknown) {
                const errorMessage =
                    (error as { message?: string })?.message ||
                    t('auth.loginFailedTryAgain', 'Login failed. Please try again.');
                setValidationErrors({ general: errorMessage });
                notificationService.error(errorMessage, { duration: 10000 });
            } finally {
                setIsLoading(false);
            }
            return;
        }

        // Step 1: credential validation (probes for 2FA)
        if (!username.trim()) return setValidationErrors({ username: 'Username is required' });
        if (!password.trim()) return setValidationErrors({ password: 'Password is required' });
        setIsLoading(true);
        playClickSound();
        try {
            const probe = await authService.validateCredentialsWithAPI(username.trim(), password);
            if (!probe.success) {
                const errorMessage = probe.message || 'Login failed';
                setValidationErrors({ general: errorMessage });
                notificationService.error(errorMessage, { duration: 10000 });
                return;
            }

            if (!probe.two_factor_enabled) {
                // Token already stored by the credential probe.
                await Promise.resolve(
                    onAuthenticate({ username: username.trim(), email: '', password, emailCode: '', phone: '', phoneCode: '' } as UserCredentials)
                );
                return;
            }

            await enterUsername2FA({
                email: probe.email,
                maskedEmail: probe.maskedEmail,
                codeSent: probe.codeSent,
                cooldownSeconds: probe.cooldownSeconds
            });
        } catch (error: unknown) {
            const errorMessage =
                (error as { message?: string })?.message || 'Login failed. Please try again.';
            setValidationErrors({ general: errorMessage });
            notificationService.error(errorMessage, { duration: 10000 });
        } finally {
            setIsLoading(false);
        }
    };

    const goToForgot = () => {
        if (apiBusy) return;
        const channel: ForgotChannel = activeTab === 'phone' ? 'phone' : 'email';
        setIsForgotMode(true);
        setForgotStep(1);
        setForgotChannel(channel);
        setForgotContactTouched(false);
        setForgotOtp('');
        setForgotRequestToken('');
        setForgotVerifiedToken('');
        setForgotNewPassword('');
        setForgotConfirmPassword('');
        setForgotOtpCooldown(300);
        setForgotResendCooldown(0);
        setValidationErrors({});
        if (channel === 'phone' && phoneNumber.trim()) {
            setForgotContact(phoneNumber.trim());
        } else if (channel === 'email' && email.trim()) {
            setForgotContact(email.trim());
        } else {
            setForgotContact('');
        }
    };

    const forgotContactFormatError = useMemo(() => {
        const formatError =
            forgotChannel === 'email'
                ? getForgotEmailFormatError(forgotContact)
                : getForgotPhoneFormatError(forgotContact);
        if (!formatError) return null;
        if (forgotContactTouched || forgotContact.trim()) return formatError;
        return null;
    }, [forgotChannel, forgotContact, forgotContactTouched]);

    const forgotContactDisplayError = validationErrors.forgotContact || forgotContactFormatError;

    const canSendForgotCode =
        isForgotMode &&
        forgotStep === 1 &&
        isForgotContactFormatValid(forgotChannel, forgotContact) &&
        !apiBusy;

    const handleForgotRequest = async () => {
        const contact = forgotContact.trim();
        setForgotContactTouched(true);
        const formatError =
            forgotChannel === 'email'
                ? getForgotEmailFormatError(contact)
                : getForgotPhoneFormatError(contact);
        if (formatError) {
            setValidationErrors((prev) => ({ ...prev, forgotContact: formatError }));
            return;
        }

        setIsLoading(true);
        setValidationErrors((prev) => {
            const next = { ...prev };
            delete next.forgotContact;
            delete next.general;
            return next;
        });
        try {
            const result = await import('@/features/auth/services/passwordResetService').then((m) =>
                m.passwordResetService.requestResetByOtp(forgotChannel, contact)
            );
            const token = result.data?.token;

            if (result.success && token) {
                setForgotRequestToken(token);
                setForgotStep(2);
                const exp = (result.data as { expiresInSeconds?: number } | undefined)?.expiresInSeconds;
                setForgotOtpCooldown(typeof exp === 'number' && exp > 0 ? exp : 600);
                const nextResend = (result.data as { next_resend_at?: string } | undefined)?.next_resend_at;
                if (nextResend) {
                    const remaining = Math.ceil((new Date(nextResend).getTime() - Date.now()) / 1000);
                    setForgotResendCooldown(remaining > 0 ? remaining : FORGOT_RESEND_COOLDOWN_SECONDS);
                } else {
                    setForgotResendCooldown(FORGOT_RESEND_COOLDOWN_SECONDS);
                }
                setForgotOtp('');
                notificationService.success(
                    forgotStep === 2 ? 'A new verification code has been sent' : 'Verification code sent'
                );
                return;
            }

            if (result.success && !token) {
                const msg = result.message || getForgotNotRegisteredMessage(forgotChannel);
                setValidationErrors({ general: msg });
                notificationService.info(msg, { duration: 6000 });
                return;
            }

            const apiFormatError =
                result.statusCode === 422 &&
                (result.message?.includes('email') || result.message?.includes('phone'));
            if (apiFormatError) {
                setValidationErrors({ forgotContact: result.message });
                return;
            }

            setValidationErrors({ general: result.message || 'Failed to request OTP' });
            notificationService.error(result.message || 'Failed to request OTP');
        } finally {
            setIsLoading(false);
        }
    };

    const handleForgotVerifyOtp = async () => {
        if (forgotOtp.trim().length !== 6) {
            setValidationErrors({ forgotOtp: 'OTP must be 6 digits' });
            return;
        }
        setIsLoading(true);
        setValidationErrors({});
        try {
            const result = await import('@/features/auth/services/passwordResetService').then((m) =>
                m.passwordResetService.verifyResetOtp(forgotRequestToken, forgotOtp.trim())
            );
            const verifiedToken = result.data?.token;
            if (!result.success || !verifiedToken) {
                const msg = result.message || 'Invalid or expired OTP.';
                setValidationErrors({ general: msg, forgotOtp: msg });
                notificationService.error(msg, { duration: 8000 });
                return;
            }
            setForgotVerifiedToken(verifiedToken);
            setForgotStep(3);
            notificationService.success('OTP verified. Choose a new password.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleForgotSetPassword = async () => {
        if (!forgotNewPassword.trim()) {
            setValidationErrors({ forgotNewPassword: 'New password is required' });
            return;
        }
        if (forgotNewPassword !== forgotConfirmPassword) {
            setValidationErrors({ forgotConfirmPassword: 'Passwords do not match' });
            return;
        }
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        if (!passwordRegex.test(forgotNewPassword)) {
            setValidationErrors({
                forgotNewPassword:
                    'Password must be at least 8 characters with uppercase, lowercase, number, and special character.',
            });
            return;
        }
        setIsLoading(true);
        setValidationErrors({});
        try {
            const result = await import('@/features/auth/services/passwordResetService').then((m) =>
                m.passwordResetService.confirmResetByOtp(forgotVerifiedToken, forgotNewPassword)
            );
            if (!result.success) {
                const msg = result.message || 'Failed to reset password.';
                setValidationErrors({ general: msg });
                notificationService.error(msg, { duration: 8000 });
                return;
            }
            notificationService.success(result.message || 'Password reset successfully. You can sign in now.');
            setIsForgotMode(false);
            setForgotStep(1);
            setActiveTab('username');
        } finally {
            setIsLoading(false);
        }
    };

    const actionText = selectedAction === 'deposit' ? 'Deposit' : selectedAction === 'withdrawal' ? 'Withdrawal' : 'Login';
    const actionColor = selectedAction === 'deposit' ? 'from-green-600 to-emerald-600' : selectedAction === 'withdrawal' ? 'from-blue-600 to-cyan-600' : 'from-green-600 to-emerald-600';

    // Determine title and subtitle for AuthHeader
    const headerTitle = isForgotMode
        ? t('authHeader.forgotPassword')
        : selectedProvider
          ? selectedProvider.name.toUpperCase()
          : t('authHeader.userLogin');
    const headerSubtitle = isForgotMode
        ? t('authHeader.forgotSubtitle')
        : selectedAction
          ? `Please verify your identity to ${actionText.toLowerCase()}`
          : t('authHeader.verifyIdentitySubtitle');

    return (
        <div
            {...fullScreenBg}
            className={`${fullScreenBg.className} flex flex-col overflow-x-hidden min-w-0 ${hideHeader ? 'h-full min-h-0' : 'min-h-screen'}`}
        >
            {!hideHeader && (
                <AuthHeader
                    title={headerTitle}
                    subtitle={headerSubtitle}
                />
            )}

            {/* Main Content - fills body; scrolls on small height */}
            <div className={`flex-1 flex justify-center min-h-0 ${hideHeader ? 'overflow-auto p-3 sm:p-4 md:p-5 lg:p-6' : 'p-4 sm:p-5 lg:p-6'}`}>
                <div className="w-full max-w-2xl mx-auto px-2 sm:px-4 lg:px-5 py-1 sm:py-3">
                    {/* USER LOGIN Title - Mobile only */}
                    <div className="flex flex-col items-center gap-1 mb-2 sm:mb-3 md:hidden">
                        <h1 className="text-white text-xl sm:text-2xl font-black italic tracking-wide text-center">
                            {isForgotMode ? t('authHeader.forgotPassword') : t('authHeader.userLogin')}
                        </h1>
                        <div className="h-0.5 w-12 sm:w-14 bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 rounded-full" />
                        <p className="text-white/60 text-xs sm:text-sm font-semibold tracking-wider text-center">
                            {isForgotMode ? t('authHeader.forgotSubtitle') : t('authHeader.verifyIdentitySubtitle')}
                        </p>
                    </div>

                    {selectedAction && (
                        <div className="flex items-center justify-center gap-1.5 mb-2 sm:mb-3">
                            <span className="text-xs sm:text-sm text-purple-200">Action:</span>
                            <span className={`px-2 py-0.5 rounded-full bg-gradient-to-r ${actionColor} text-white font-bold text-xs sm:text-sm`}>
                                {actionText}
                            </span>
                        </div>
                    )}

                    {!isWalletFlow && totalAmount > 0 && (
                        <div className="mb-2 sm:mb-3 inline-block px-3 py-1.5 bg-gradient-to-r from-green-500/20 to-emerald-500/20 backdrop-blur-md rounded-lg border border-green-400/30">
                            <p className="text-green-200 text-[10px] sm:text-xs font-semibold">
                                You're about to deposit <span className="text-green-100 font-bold text-xs sm:text-sm">${Math.floor(totalAmount)}</span> to the games
                            </p>
                        </div>
                    )}

                    {/* Tab Navigation — dimmed while forgot-account flow is open */}
                    <div
                        className={`grid grid-cols-3 gap-1.5 sm:gap-2 w-full mb-2 sm:mb-3 transition-opacity ${isForgotMode || apiBusy ? 'opacity-45 pointer-events-none' : ''}`}
                        aria-hidden={isForgotMode}
                    >
                        <button
                            type="button"
                            onClick={() => handleTabChange('username')}
                            disabled={apiBusy}
                            className={`py-2 sm:py-2.5 px-3 font-semibold text-sm sm:text-base transition-all duration-200 flex items-center justify-center gap-1.5 ${activeTab === 'username' ? 'text-white' : 'text-white/65 hover:text-white/90'} disabled:cursor-not-allowed`}
                            style={activeTab === 'username' ? tabActiveStyle : tabInactiveStyle}
                        >
                            <UserIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                            <span>Username</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => handleTabChange('phone')}
                            disabled={apiBusy}
                            className={`py-2 sm:py-2.5 px-3 font-semibold text-sm sm:text-base transition-all duration-200 flex items-center justify-center gap-1.5 ${activeTab === 'phone' ? 'text-white' : 'text-white/65 hover:text-white/90'} disabled:cursor-not-allowed`}
                            style={activeTab === 'phone' ? tabActiveStyle : tabInactiveStyle}
                        >
                            <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                            <span>Phone</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => handleTabChange('email')}
                            disabled={apiBusy}
                            className={`py-2 sm:py-2.5 px-3 font-semibold text-sm sm:text-base transition-all duration-200 flex items-center justify-center gap-1.5 ${activeTab === 'email' ? 'text-white' : 'text-white/65 hover:text-white/90'} disabled:cursor-not-allowed`}
                            style={activeTab === 'email' ? tabActiveStyle : tabInactiveStyle}
                        >
                            <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                            <span>Email</span>
                        </button>
                    </div>

                    {/* Form card */}
                    <div
                        className="flex flex-col items-start w-full p-3 sm:p-4 md:p-5 gap-3 sm:gap-4 rounded-xl sm:rounded-2xl overflow-hidden"
                        style={{
                            ...loginPanelStyle,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            alignSelf: 'stretch',
                        }}
                    >

                        {isForgotMode ? (
                            <div className="w-full space-y-3">
                                <h2 className="text-base sm:text-lg font-bold text-white tracking-wide">
                                    {t('authHeader.forgotPassword')}
                                </h2>
                                <div className="text-[10px] sm:text-xs text-white/85 flex items-center justify-between gap-2">
                                    <span className={forgotStep === 1 ? 'text-cyan-300/95' : 'text-white/45'}>1 Contact</span>
                                    <span className={forgotStep === 2 ? 'text-cyan-300/95' : 'text-white/45'}>2 OTP</span>
                                    <span className={forgotStep === 3 ? 'text-cyan-300/95' : 'text-white/45'}>3 Password</span>
                                </div>
                                {forgotStep === 1 && (
                                    <p className="text-[10px] sm:text-xs text-white/65 leading-snug">
                                        {forgotChannel === 'email'
                                            ? 'Enter the email address registered on your account.'
                                            : 'Enter the phone number registered on your account.'}
                                    </p>
                                )}
                                {validationErrors.general && (
                                    <div
                                        className="w-full p-2.5 rounded-lg border text-[11px] sm:text-xs leading-snug bg-amber-500/10 border-amber-400/35 text-amber-100/95"
                                        role="alert"
                                    >
                                        {validationErrors.general}
                                    </div>
                                )}
                                {forgotStep === 1 && (
                                    <div className="space-y-2">
                                        <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                                            <button
                                                type="button"
                                                disabled={apiBusy}
                                                onClick={() => {
                                                    setForgotChannel('email');
                                                    setForgotContactTouched(false);
                                                }}
                                                className={`py-2 sm:py-2.5 px-3 font-semibold text-sm sm:text-base transition-all duration-200 flex items-center justify-center gap-1.5 ${
                                                    forgotChannel === 'email' ? 'text-white' : 'text-white/65 hover:text-white/90'
                                                } disabled:cursor-not-allowed`}
                                                style={forgotChannel === 'email' ? tabActiveStyle : tabInactiveStyle}
                                            >
                                                <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                                                <span>Email</span>
                                            </button>
                                            <button
                                                type="button"
                                                disabled={apiBusy}
                                                onClick={() => {
                                                    setForgotChannel('phone');
                                                    setForgotContactTouched(false);
                                                }}
                                                className={`py-2 sm:py-2.5 px-3 font-semibold text-sm sm:text-base transition-all duration-200 flex items-center justify-center gap-1.5 ${
                                                    forgotChannel === 'phone' ? 'text-white' : 'text-white/65 hover:text-white/90'
                                                } disabled:cursor-not-allowed`}
                                                style={forgotChannel === 'phone' ? tabActiveStyle : tabInactiveStyle}
                                            >
                                                <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                                                <span>Phone</span>
                                            </button>
                                        </div>
                                        <div className="flex flex-col gap-1.5 w-full">
                                            <label htmlFor="forgot-contact" className="block text-xs sm:text-sm font-medium text-white">
                                                {forgotChannel === 'email' ? 'Email address' : 'Phone number'}
                                            </label>
                                            {forgotChannel === 'phone' ? (
                                                <div
                                                    className={`relative w-full flex items-center rounded-lg border border-white/10 bg-white/10 backdrop-blur-md min-h-[40px] sm:min-h-[44px] overflow-visible px-2.5 sm:px-3 ${
                                                        forgotContactDisplayError ? 'border-red-500/50' : ''
                                                    }`}
                                                >
                                                    <PhoneInput
                                                        id="forgot-contact"
                                                        international
                                                        defaultCountry="US"
                                                        countryOptionsOrder={['US', 'CA', 'GB', '…']}
                                                        placeholder="Enter your phone number"
                                                        value={forgotContact || undefined}
                                                        onChange={(v) => setForgotContact(v ?? '')}
                                                        onBlur={() => setForgotContactTouched(true)}
                                                        countrySelectComponent={CountrySelectWithSearch}
                                                        className="PhoneInputAuth w-full"
                                                        numberInputProps={{
                                                            'aria-invalid': !!forgotContactDisplayError,
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <input
                                                    id="forgot-contact"
                                                    type="email"
                                                    autoComplete="email"
                                                    placeholder="name@example.com"
                                                    value={forgotContact}
                                                    onChange={(e) => setForgotContact(e.target.value)}
                                                    onBlur={() => setForgotContactTouched(true)}
                                                    className={`w-full rounded-lg border border-white/10 bg-white/10 px-3 py-2.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/40 ${
                                                        forgotContactDisplayError ? 'border-red-500/50' : ''
                                                    }`}
                                                    aria-invalid={!!forgotContactDisplayError}
                                                />
                                            )}
                                            {forgotContactDisplayError && (
                                                <p className="text-xs text-red-400" role="alert">
                                                    {forgotContactDisplayError}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:items-stretch">
                                            <button
                                                type="button"
                                                disabled={isLoading}
                                                onClick={() => {
                                                    setIsForgotMode(false);
                                                    setForgotContactTouched(false);
                                                }}
                                                className="rounded-lg px-4 py-2.5 border border-white/12 bg-white/[0.06] text-white/85 text-sm font-medium hover:bg-white/10 hover:border-cyan-500/25 transition-colors sm:w-auto shrink-0 disabled:opacity-45 disabled:cursor-not-allowed"
                                            >
                                                Back
                                            </button>
                                            <button
                                                type="button"
                                                disabled={!canSendForgotCode}
                                                onClick={handleForgotRequest}
                                                className="flex-1 rounded-lg py-2.5 text-white font-bold text-sm shadow-lg transition-opacity hover:opacity-95 active:scale-[0.99] disabled:opacity-45 disabled:cursor-not-allowed"
                                                style={primaryCtaStyle}
                                            >
                                                {isLoading ? (
                                                    <span className="flex items-center justify-center gap-2">
                                                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden />
                                                        Sending...
                                                    </span>
                                                ) : (
                                                    'SEND CODE'
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {forgotStep === 2 && (
                                    <div className="space-y-2">
                                        <div className="text-sm text-white/75">
                                            Code expires in{' '}
                                            <span className="text-cyan-300 tabular-nums">
                                                {Math.floor(forgotOtpCooldown / 60)}:{String(forgotOtpCooldown % 60).padStart(2, '0')}
                                            </span>
                                        </div>
                                        <div className="flex gap-2">
                                            <input
                                                value={forgotOtp}
                                                onChange={(e) => {
                                                    setForgotOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                                                    if (validationErrors.forgotOtp || validationErrors.general) {
                                                        setValidationErrors((prev) => {
                                                            const next = { ...prev };
                                                            delete next.forgotOtp;
                                                            delete next.general;
                                                            return next;
                                                        });
                                                    }
                                                }}
                                                className={`flex-1 min-w-0 rounded-lg border bg-white/10 px-2 py-2.5 text-center tracking-[0.35em] text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 ${
                                                    validationErrors.forgotOtp
                                                        ? 'border-red-400/60 focus:ring-red-500/50'
                                                        : 'border-white/10 focus:ring-purple-500/40'
                                                }`}
                                                placeholder="000000"
                                                inputMode="numeric"
                                                aria-invalid={!!validationErrors.forgotOtp}
                                            />
                                            <button
                                                type="button"
                                                disabled={forgotResendCooldown > 0 || isLoading}
                                                onClick={handleForgotRequest}
                                                className="shrink-0 rounded-lg px-3 sm:px-4 py-2.5 border border-white/12 bg-white/[0.06] text-slate-100 text-xs sm:text-sm font-medium disabled:opacity-45 disabled:cursor-not-allowed hover:bg-white/10 hover:border-cyan-500/25 transition-colors tabular-nums min-w-[5.5rem]"
                                            >
                                                {isLoading ? (
                                                    <span className="inline-flex items-center justify-center">
                                                        <span className="w-3.5 h-3.5 border-2 border-slate-200 border-t-transparent rounded-full animate-spin" aria-hidden />
                                                    </span>
                                                ) : forgotResendCooldown > 0 ? (
                                                    `RESEND (${forgotResendCooldown}s)`
                                                ) : (
                                                    'RESEND'
                                                )}
                                            </button>
                                        </div>
                                        {validationErrors.forgotOtp && (
                                            <p className="text-[11px] sm:text-xs text-red-400" role="alert">
                                                {validationErrors.forgotOtp}
                                            </p>
                                        )}
                                        <button
                                            type="button"
                                            disabled={isLoading}
                                            onClick={() => void handleForgotVerifyOtp()}
                                            className="w-full rounded-lg py-2.5 text-white font-bold shadow-lg transition-opacity hover:opacity-95 disabled:opacity-70 disabled:cursor-not-allowed"
                                            style={primaryCtaStyle}
                                        >
                                            {isLoading ? (
                                                <span className="flex items-center justify-center gap-2">
                                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden />
                                                    Working...
                                                </span>
                                            ) : (
                                                'VERIFY CODE'
                                            )}
                                        </button>
                                    </div>
                                )}
                                {forgotStep === 3 && (
                                    <div className="space-y-2">
                                        <div className="flex flex-col gap-1.5">
                                            <label htmlFor="forgot-new-password" className="block text-xs sm:text-sm font-medium text-white">
                                                New password
                                            </label>
                                            <div className="relative">
                                                <input
                                                    id="forgot-new-password"
                                                    type={showForgotNewPassword ? 'text' : 'password'}
                                                    value={forgotNewPassword}
                                                    onChange={(e) => setForgotNewPassword(e.target.value)}
                                                    className="w-full rounded-lg border border-white/10 bg-white/10 px-3 py-2.5 pr-10 text-sm text-white"
                                                    autoComplete="new-password"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowForgotNewPassword((v) => !v)}
                                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/55"
                                                >
                                                    {showForgotNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                                </button>
                                            </div>
                                            {validationErrors.forgotNewPassword && (
                                                <p className="text-xs text-red-400">{validationErrors.forgotNewPassword}</p>
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                            <label htmlFor="forgot-confirm-password" className="block text-xs sm:text-sm font-medium text-white">
                                                Confirm password
                                            </label>
                                            <input
                                                id="forgot-confirm-password"
                                                type={showForgotNewPassword ? 'text' : 'password'}
                                                value={forgotConfirmPassword}
                                                onChange={(e) => setForgotConfirmPassword(e.target.value)}
                                                className="w-full rounded-lg border border-white/10 bg-white/10 px-3 py-2.5 text-sm text-white"
                                                autoComplete="new-password"
                                            />
                                            {validationErrors.forgotConfirmPassword && (
                                                <p className="text-xs text-red-400">{validationErrors.forgotConfirmPassword}</p>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            disabled={isLoading}
                                            onClick={() => void handleForgotSetPassword()}
                                            className="w-full rounded-lg py-2.5 text-white font-bold shadow-lg transition-opacity hover:opacity-95 disabled:opacity-70 disabled:cursor-not-allowed"
                                            style={primaryCtaStyle}
                                        >
                                            {isLoading ? 'Saving...' : 'RESET PASSWORD'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                        <>
                        {activeTab === 'username' && (
                            <form onSubmit={handleUsernameLogin} className="space-y-3 w-full">
                                {username2FA ? (
                                    <>
                                        <div className="flex items-center gap-2 w-full">
                                            <Lock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-400 flex-shrink-0" />
                                            <span className="text-white font-semibold text-sm sm:text-base">
                                                {t('auth.twoFactorTitle', 'Two-factor authentication')}
                                            </span>
                                        </div>
                                        <p className="text-xs sm:text-sm text-white/70 leading-snug">
                                            {twoFactorMaskedEmail
                                                ? t('auth.twoFactorHint', 'Enter the 6-digit code sent to {{email}}', {
                                                      email: twoFactorMaskedEmail,
                                                  })
                                                : t(
                                                      'auth.twoFactorHintGeneric',
                                                      'Enter the 6-digit code sent to your registered email'
                                                  )}
                                        </p>
                                        <div className="flex flex-col gap-1.5 w-full">
                                            <label htmlFor="username-2fa-code" className="block text-xs sm:text-sm font-medium text-white">
                                                Verification Code
                                            </label>
                                            <div className="flex flex-wrap gap-1.5 sm:gap-2 items-center w-full">
                                                <input
                                                    id="username-2fa-code"
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength={6}
                                                    autoComplete="one-time-code"
                                                    value={emailCode}
                                                    onChange={(e) => {
                                                        setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                                                        setValidationErrors((prev) => {
                                                            if (!prev.emailCode && !prev.general) return prev;
                                                            const next = { ...prev };
                                                            delete next.emailCode;
                                                            delete next.general;
                                                            return next;
                                                        });
                                                    }}
                                                    className={`flex-1 min-w-0 px-2.5 py-2 rounded-lg bg-white/10 backdrop-blur-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-inset text-sm sm:text-base text-center font-mono tracking-widest placeholder:text-xs sm:placeholder:text-base ${validationErrors.emailCode ? 'focus:ring-red-500' : 'focus:ring-purple-500'}`}
                                                    placeholder="000000"
                                                    required
                                                />
                                                <button
                                                    type="button"
                                                    onClick={handleResendUsername2FACode}
                                                    disabled={isSendingCode || emailCooldown > 0 || isLoading}
                                                    className={`shrink-0 px-2.5 sm:px-3 py-2 rounded-lg font-semibold text-xs sm:text-sm transition-all min-w-[80px] sm:min-w-[90px] ${isSendingCode || emailCooldown > 0 || isLoading ? 'bg-gray-600 text-gray-400 cursor-not-allowed' : 'text-black active:scale-95'}`}
                                                    style={isSendingCode || emailCooldown > 0 || isLoading ? undefined : { background: 'linear-gradient(180deg, #FFED4A 0%, #FFCC00 100%)', boxShadow: '0 0 16px #FFCC0066' }}
                                                >
                                                    {isSendingCode ? (
                                                        <div className="w-3 h-3 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto" />
                                                    ) : emailCooldown > 0 ? (
                                                        <span>{emailCooldown}s</span>
                                                    ) : (
                                                        <span className="font-bold">{t('auth.resendCode', 'Resend')}</span>
                                                    )}
                                                </button>
                                            </div>
                                            {validationErrors.emailCode && (
                                                <p className="text-xs text-red-400">{validationErrors.emailCode}</p>
                                            )}
                                        </div>
                                        {validationErrors.general && (
                                            <div
                                                className="p-2 bg-red-500/20 border border-red-500/50 rounded-lg"
                                                role="alert"
                                            >
                                                <p className="text-red-400 text-xs sm:text-sm whitespace-pre-line">
                                                    {validationErrors.general}
                                                </p>
                                            </div>
                                        )}
                                        <div className="flex flex-col w-full gap-2.5 pt-0.5">
                                            <button
                                                type="submit"
                                                disabled={isLoading || isSendingCode || emailCode.trim().length !== 6}
                                                className={`w-full rounded-lg py-2.5 text-white font-bold transition-opacity ${isLoading || isSendingCode || emailCode.trim().length !== 6 ? 'opacity-60 cursor-not-allowed' : ''}`}
                                                style={primaryCtaStyle}
                                            >
                                                {isLoading ? (
                                                    <span className="flex items-center justify-center gap-2">
                                                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden />
                                                        <span>Verifying...</span>
                                                    </span>
                                                ) : (
                                                    t('auth.verifyAndSignIn', 'Verify & sign in')
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleBackToPasswordStep}
                                                disabled={isLoading}
                                                className="text-sm text-fuchsia-300/90 hover:text-fuchsia-200 underline-offset-2 hover:underline disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline self-center"
                                            >
                                                {t('auth.backToPassword', 'Back')}
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex flex-col gap-1.5 w-full">
                                            <label htmlFor="login-username" className="block text-xs sm:text-sm font-medium text-white">
                                                Username
                                            </label>
                                            <div
                                                className={`relative w-full flex items-center rounded-lg border border-white/10 bg-white/10 backdrop-blur-md min-h-[40px] sm:min-h-[44px] overflow-hidden ${validationErrors.username ? 'border-red-500/50' : ''}`}
                                            >
                                                <div className="absolute inset-y-0 left-0 pl-2.5 sm:pl-3 flex items-center pointer-events-none">
                                                    <UserIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" aria-hidden />
                                                </div>
                                                <input
                                                    id="login-username"
                                                    value={username}
                                                    onChange={(e) => {
                                                        setUsername(e.target.value);
                                                        setValidationErrors((prev) => {
                                                            if (!prev.username && !prev.general) return prev;
                                                            const next = { ...prev };
                                                            delete next.username;
                                                            delete next.general;
                                                            return next;
                                                        });
                                                    }}
                                                    autoComplete="username"
                                                    aria-invalid={!!validationErrors.username}
                                                    className={`block w-full min-h-[40px] sm:min-h-[44px] pl-7 sm:pl-9 pr-2.5 py-0 border-none bg-transparent text-white placeholder-gray-400 focus:outline-none text-sm sm:text-base placeholder:text-xs sm:placeholder:text-base ${validationErrors.username ? 'text-red-400' : ''}`}
                                                    placeholder="Enter your username"
                                                />
                                            </div>
                                            {validationErrors.username && (
                                                <p className="text-xs text-red-400">{validationErrors.username}</p>
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-1.5 w-full">
                                            <label htmlFor="login-password" className="block text-xs sm:text-sm font-medium text-white">
                                                Password
                                            </label>
                                            <div
                                                className={`relative w-full flex items-center rounded-lg border border-white/10 bg-white/10 backdrop-blur-md min-h-[40px] sm:min-h-[44px] overflow-hidden ${validationErrors.password ? 'border-red-500/50' : ''}`}
                                            >
                                                <div className="absolute inset-y-0 left-0 pl-2.5 sm:pl-3 flex items-center pointer-events-none">
                                                    <Lock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" aria-hidden />
                                                </div>
                                                <input
                                                    id="login-password"
                                                    type={showPassword ? 'text' : 'password'}
                                                    value={password}
                                                    onChange={(e) => {
                                                        setPassword(e.target.value);
                                                        setValidationErrors((prev) => {
                                                            if (!prev.password && !prev.general) return prev;
                                                            const next = { ...prev };
                                                            delete next.password;
                                                            delete next.general;
                                                            return next;
                                                        });
                                                    }}
                                                    autoComplete="current-password"
                                                    aria-invalid={!!validationErrors.password}
                                                    className={`block w-full min-h-[40px] sm:min-h-[44px] pl-7 sm:pl-9 pr-10 sm:pr-11 py-0 border-none bg-transparent text-white placeholder-gray-400 focus:outline-none text-sm sm:text-base placeholder:text-xs sm:placeholder:text-base ${validationErrors.password ? 'text-red-400' : ''}`}
                                                    placeholder="Enter your password"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword((v) => !v)}
                                                    className="absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2 text-white/55 hover:text-white/90 p-0.5"
                                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                >
                                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                                </button>
                                            </div>
                                            {validationErrors.password && (
                                                <p className="text-xs text-red-400">{validationErrors.password}</p>
                                            )}
                                        </div>
                                        <div className="text-right">
                                            <button
                                                type="button"
                                                onClick={goToForgot}
                                                disabled={apiBusy}
                                                className="text-sm text-fuchsia-300/90 hover:text-fuchsia-200 underline-offset-2 hover:underline disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
                                            >
                                                Forgot password?
                                            </button>
                                        </div>
                                        {validationErrors.general && (
                                            <div
                                                className="p-2 bg-red-500/20 border border-red-500/50 rounded-lg"
                                                role="alert"
                                            >
                                                <p className="text-red-400 text-xs sm:text-sm whitespace-pre-line">
                                                    {validationErrors.general}
                                                </p>
                                            </div>
                                        )}
                                        <div className="flex flex-col w-full gap-2.5 pt-0.5">
                                            <button
                                                type="submit"
                                                disabled={isLoading}
                                                className={`w-full rounded-lg py-2.5 text-sm sm:text-base text-white font-bold transition-opacity ${isLoading ? 'opacity-80 cursor-not-allowed' : ''}`}
                                                style={primaryCtaStyle}
                                            >
                                                {isLoading ? (
                                                    <span className="flex items-center justify-center gap-2">
                                                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden />
                                                        <span>Logging in...</span>
                                                    </span>
                                                ) : (
                                                    'LOGIN'
                                                )}
                                            </button>
                                            <p className="text-center text-xs sm:text-sm text-white/70 px-1 leading-relaxed w-full">
                                                No account? You can also login via{' '}
                                                <button
                                                    type="button"
                                                    disabled={apiBusy}
                                                    onClick={() => handleTabChange('phone')}
                                                    className="underline underline-offset-2 text-sky-300 hover:text-sky-200 disabled:opacity-45 disabled:cursor-not-allowed p-0 border-0 bg-transparent font-inherit cursor-pointer"
                                                >
                                                    phone number
                                                </button>
                                                {' '}or{' '}
                                                <button
                                                    type="button"
                                                    disabled={apiBusy}
                                                    onClick={() => handleTabChange('email')}
                                                    className="underline underline-offset-2 text-sky-300 hover:text-sky-200 disabled:opacity-45 disabled:cursor-not-allowed p-0 border-0 bg-transparent font-inherit cursor-pointer"
                                                >
                                                    email
                                                </button>
                                                .
                                            </p>
                                        </div>
                                    </>
                                )}
                            </form>
                        )}
                        {/* Phone Login Tab */}
                        {activeTab === 'phone' && (
                            <form onSubmit={handlePhoneLogin} className="space-y-2.5 sm:space-y-3 w-full">
                                <div className="flex items-center gap-2 w-full">
                                    <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-400 flex-shrink-0" />
                                    <span className="text-white font-semibold text-sm sm:text-base">Phone Login</span>
                                </div>
                                <div className="flex flex-col gap-1.5 w-full">
                                    <label htmlFor="phone" className="block text-xs sm:text-sm font-medium text-white">Phone Number</label>
                                    <div className={`relative w-full flex items-center rounded-lg border border-white/10 bg-white/10 backdrop-blur-md min-h-[40px] sm:min-h-[44px] overflow-visible px-2.5 sm:px-3 ${validationErrors.phone ? 'border-red-500/50' : ''}`}>
                                        <PhoneInput
                                            id="phone"
                                            international={false}
                                            defaultCountry="US"
                                            countryOptionsOrder={['US', 'CA', 'GB', '…']}
                                            placeholder="Enter your phone number"
                                            value={phoneNumber || undefined}
                                            onChange={(v) => {
                                                setPhoneNumber(v ?? '');
                                                setValidationErrors((prev) => (prev.phone ? { ...prev, phone: undefined } : prev));
                                            }}
                                            countrySelectComponent={CountrySelectWithSearch}
                                            className="PhoneInputAuth w-full"
                                            numberInputProps={{ required: true, 'aria-invalid': !!validationErrors.phone }}
                                        />
                                    </div>
                                    {validationErrors.phone && <p className="text-xs text-red-400">{validationErrors.phone}</p>}
                                    <p className="text-xs text-white/60">Select country and enter your phone number</p>
                                </div>

                                <div className="flex flex-col gap-1.5 w-full">
                                    <label htmlFor="phoneCode" className="block text-xs sm:text-sm font-medium text-white">Verification Code</label>
                                    <div className="flex flex-wrap gap-1.5 sm:gap-2 items-center w-full">
                                        <input
                                            id="phoneCode"
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={6}
                                            value={phoneCode}
                                            onChange={(e) => { setPhoneCode(e.target.value.replace(/\D/g, '')); setValidationErrors({}); }}
                                            className={`flex-1 min-w-0 px-2.5 py-2 rounded-lg bg-white/10 backdrop-blur-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-inset text-sm sm:text-base text-center font-mono tracking-widest placeholder:text-xs sm:placeholder:text-base ${validationErrors.phoneCode ? 'focus:ring-red-500' : 'focus:ring-purple-500'}`}
                                            placeholder="000000"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={handleSendPhoneCode}
                                            disabled={isSendingCode || phoneCooldown > 0 || !phoneNumber.trim() || isLoading}
                                            className={`shrink-0 px-2.5 sm:px-3 py-2 rounded-lg font-semibold text-xs sm:text-sm transition-all min-w-[80px] sm:min-w-[90px] ${isSendingCode || phoneCooldown > 0 || !phoneNumber.trim() || isLoading ? 'bg-gray-600 text-gray-400 cursor-not-allowed' : 'text-black active:scale-95'}`}
                                            style={isSendingCode || phoneCooldown > 0 || !phoneNumber.trim() || isLoading ? undefined : { background: 'linear-gradient(180deg, #FFED4A 0%, #FFCC00 100%)', boxShadow: '0 0 16px #FFCC0066' }}
                                        >
                                            {isSendingCode ? <div className="w-3 h-3 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto" /> : phoneCooldown > 0 ? <span>{phoneCooldown}s</span> : <span className="font-bold">GET CODE</span>}
                                        </button>
                                    </div>
                                    {validationErrors.phoneCode && <p className="text-xs text-red-400">{validationErrors.phoneCode}</p>}
                                </div>

                                {validationErrors.general && (
                                    <div className="p-2 bg-red-500/20 border border-red-500/50 rounded-lg">
                                        <p className="text-red-400 text-xs sm:text-sm">{validationErrors.general}</p>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={isLoading || isSendingCode || !phoneCode.trim() || phoneCode.length !== 6}
                                    className={`w-full rounded-lg font-bold text-sm sm:text-base py-2.5 sm:py-3 text-white transition-all ${isLoading || isSendingCode || !phoneCode.trim() || phoneCode.length !== 6 ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-90 active:scale-95'}`}
                                    style={{ background: 'linear-gradient(180deg, #D946EF 0%, #7B2CBF 100%)', boxShadow: '0 0 16px #7B2CBF80' }}
                                >
                                    {isLoading ? <div className="flex items-center justify-center gap-1.5"><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /><span>Logging in...</span></div> : 'LOGIN'}
                                </button>
                            </form>
                        )}

                        {activeTab === 'email' && (
                            <form onSubmit={handleEmailLogin} className="space-y-2.5 sm:space-y-3 w-full">
                                <div className="flex items-center gap-2 w-full">
                                    <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-400 flex-shrink-0" />
                                    <span className="text-white font-semibold text-sm sm:text-base">Email Login</span>
                                </div>

                                <div className="flex flex-col gap-1.5 w-full">
                                    <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-white">Email Address</label>
                                    <div className="relative w-full flex items-center rounded-lg border border-white/10 bg-white/10 backdrop-blur-md min-h-[40px] sm:min-h-[44px] overflow-hidden">
                                        <div className="absolute inset-y-0 left-0 pl-2.5 sm:pl-3 flex items-center pointer-events-none">
                                            <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                                        </div>
                                        <input
                                            id="email"
                                            type="email"
                                            value={email}
                                            onChange={(e) => { setEmail(e.target.value); setValidationErrors({}); }}
                                            className={`block w-full min-h-[40px] sm:min-h-[44px] pl-7 sm:pl-9 pr-2.5 py-0 border-none bg-transparent text-white placeholder-gray-400 focus:outline-none text-sm sm:text-base placeholder:text-xs sm:placeholder:text-base ${validationErrors.email ? 'text-red-400' : ''}`}
                                            placeholder="Enter your email address"
                                            required
                                        />
                                    </div>
                                    {validationErrors.email && <p className="text-xs text-red-400">{validationErrors.email}</p>}
                                </div>

                                <div className="flex flex-col gap-1.5 w-full">
                                    <label htmlFor="emailCode" className="block text-xs sm:text-sm font-medium text-white">Verification Code</label>
                                    <div className="flex flex-wrap gap-1.5 sm:gap-2 items-center w-full">
                                        <input
                                            id="emailCode"
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={6}
                                            value={emailCode}
                                            onChange={(e) => { setEmailCode(e.target.value.replace(/\D/g, '')); setValidationErrors({}); }}
                                            className={`flex-1 min-w-0 px-2.5 py-2 rounded-lg bg-white/10 backdrop-blur-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-inset text-sm sm:text-base text-center font-mono tracking-widest placeholder:text-xs sm:placeholder:text-base ${validationErrors.emailCode ? 'focus:ring-red-500' : 'focus:ring-purple-500'}`}
                                            placeholder="000000"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={handleSendEmailCode}
                                            disabled={isSendingCode || emailCooldown > 0 || !email.trim() || isLoading}
                                            className={`shrink-0 px-2.5 sm:px-3 py-2 rounded-lg font-semibold text-xs sm:text-sm transition-all min-w-[80px] sm:min-w-[90px] ${isSendingCode || emailCooldown > 0 || !email.trim() || isLoading ? 'bg-gray-600 text-gray-400 cursor-not-allowed' : 'text-black active:scale-95'}`}
                                            style={isSendingCode || emailCooldown > 0 || !email.trim() || isLoading ? undefined : { background: 'linear-gradient(180deg, #FFED4A 0%, #FFCC00 100%)', boxShadow: '0 0 16px #FFCC0066' }}
                                        >
                                            {isSendingCode ? <div className="w-3 h-3 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto" /> : emailCooldown > 0 ? <span>{emailCooldown}s</span> : <span className="font-bold">GET CODE</span>}
                                        </button>
                                    </div>
                                    {validationErrors.emailCode && <p className="text-xs text-red-400">{validationErrors.emailCode}</p>}
                                </div>

                                {validationErrors.general && (
                                    <div className="p-2 bg-red-500/20 border border-red-500/50 rounded-lg">
                                        <p className="text-red-400 text-xs sm:text-sm">{validationErrors.general}</p>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={isLoading || isSendingCode || !emailCode.trim() || emailCode.length !== 6}
                                    className={`w-full rounded-lg font-bold text-sm sm:text-base py-2.5 sm:py-3 text-white transition-all ${isLoading || isSendingCode || !emailCode.trim() || emailCode.length !== 6 ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-90 active:scale-95'}`}
                                    style={{ background: 'linear-gradient(180deg, #D946EF 0%, #7B2CBF 100%)', boxShadow: '0 0 16px #7B2CBF80' }}
                                >
                                    {isLoading ? <div className="flex items-center justify-center gap-1.5"><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /><span>Logging in...</span></div> : 'LOGIN'}
                                </button>
                            </form>
                        )}

                        </>
                        )}
                        {/* Security Notice */}
                        <div className="mt-2 sm:mt-3 w-full flex items-start gap-2 p-2.5 sm:p-3 rounded-lg bg-emerald-950/25 border border-emerald-500/20 backdrop-blur-sm">
                            <div className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0 mt-0.5 shadow-[0_0_10px_rgba(52,211,153,0.65)]" aria-hidden />
                            <div className="min-w-0 flex-1">
                                <p className="text-emerald-100 font-semibold text-xs sm:text-sm">Secure connection established</p>
                                <p className="text-emerald-200/55 text-[11px] sm:text-xs leading-snug mt-0.5">
                                    Your verification code is encrypted in transit. We never sell your personal information.
                                </p>
                            </div>
                        </div>
                    </div>
                    {/* End Authentication Form Container */}
                </div>
            </div>
        </div>
    );
}
